import { Invitation } from "@garther/interfaces";
import { Box, Button, Spinner } from "@garther/ui/components";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useInvitationStore } from "../../stores/invitation.store";

interface FriendInvitationConfirmProps {
  invitation: Invitation;
  onSuccess: () => void;
  onError: (message: string) => void;
}

export const FriendInvitationConfirm: React.FC<FriendInvitationConfirmProps> = ({
  invitation,
  onSuccess,
  onError
}) => {
  console.log("[DEBUG] FriendInvitationConfirm", invitation);
  const { t } = useTranslation();
  const { confirmInvitation, declineInvitation, isLoading, currentEvent, currentInviterProfile } =
    useInvitationStore();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      const result = await confirmInvitation(invitation.id);
      if (result) {
        onSuccess();
      } else {
        onError(t("friendInvitation.confirmFailed"));
      }
    } catch (error) {
      onError(t("friendInvitation.confirmFailed"));
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDecline = async () => {
    setIsDeclining(true);
    try {
      const result = await declineInvitation(invitation.id);
      if (result) {
        onSuccess();
      } else {
        onError(t("friendInvitation.declineFailed"));
      }
    } catch (error) {
      onError(t("friendInvitation.declineFailed"));
    } finally {
      setIsDeclining(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(date));
  };

  return (
    <div className="h-full e-center bg-background-primary">
      <div className="max-w-md w-full mx-4">
        <Box>
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full e-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            </div>
            <h1 className="heading-2 text-text-primary mb-4">{t("friendInvitation.confirmTitle")}</h1>
            <p className="body-base text-text-secondary">{t("friendInvitation.confirmDescription")}</p>
          </div>

          <div className="space-y-6">
            {/* Inviter Information */}
            {currentInviterProfile && (
              <div>
                <h3 className="heading-5 text-text-primary mb-3">{t("friendInvitation.inviterInfo")}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t("common.name")}:</span>
                    <span className="text-text-primary">{currentInviterProfile.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t("common.email")}:</span>
                    <span className="text-text-primary">{currentInviterProfile.email}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Event Details */}
            {currentEvent && (
              <div>
                <h3 className="heading-5 text-text-primary mb-3">{t("friendInvitation.eventDetails")}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t("common.date")}:</span>
                    <span className="text-text-primary">{formatDate(currentEvent.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    {/* <span className="text-text-secondary">{t('common.type')}:</span>
										<span className="text-text-primary">{currentEvent.type}</span> */}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">{t("common.status")}:</span>
                    <span className="text-text-primary">{currentEvent.status}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Invitation Details */}
            <div>
              <h3 className="heading-5 text-text-primary mb-3">{t("friendInvitation.invitationDetails")}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">{t("friendInvitation.expiresAt")}:</span>
                  <span className="text-text-primary">{formatDate(invitation.expiresAt)}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                variant="default"
                onClick={handleConfirm}
                disabled={isConfirming || isDeclining || isLoading}
                className="w-full e-center space-x-2"
              >
                {isConfirming && <Spinner className="h-4 w-4" />}
                <span>{isConfirming ? t("common.confirming") : t("friendInvitation.confirmButton")}</span>
              </Button>

              <Button
                variant="outline"
                onClick={handleDecline}
                disabled={isConfirming || isDeclining || isLoading}
                className="w-full e-center space-x-2"
              >
                {isDeclining && <Spinner className="h-4 w-4" />}
                <span>{isDeclining ? t("common.declining") : t("friendInvitation.declineButton")}</span>
              </Button>
            </div>

            {/* Additional Info */}
            <div className="text-center">
              <p className="text-xs text-text-secondary">{t("friendInvitation.confirmNote")}</p>
            </div>
          </div>
        </Box>
      </div>
    </div>
  );
};
